import { config } from "../../config";
import { isDemoSavingsUser } from "./demoSavings";

/**
 * Demo account device configuration.
 *
 * Real accounts have every meter configured individually, so a demo walkthrough hits
 * devices with no graphs, no tiles and nothing on the dashboard. For the demo user every
 * energiSync device is therefore forced to the configuration of the reference meter
 * (M630201 "Main Meter") - the same system values, graphs and home page tiles no matter
 * which device is selected. Everything else on the device (id, name, room, readings,
 * thresholds, ...) is left untouched, and non-meter devices (thermoSmarts, weather
 * sensors, environSyncs, door sensors) keep their own configuration because these
 * parameters are meter specific.
 */

// Fresh copies per device so no two devices end up sharing one object reference.
export const demoDeviceSystemValues = () => [
  "voltage",
  "powerFactor",
  "power",
  "current",
  "onlineStatus",
  "ACStatus",
  "applianceHealth",
  "unitConsumed",
  "unitSaved",
  "unitSaving",
];

export const demoDeviceGraphParameters = () => ({
  current: {},
  carbonSaved: { xAxisInterval: 1 },
  unitSaved: { xAxisInterval: 1 },
  unitConsumed: { xAxisInterval: 1 },
  power: {},
  voltage: {},
  frequency: {},
  powerFactor: {},
});

export const demoDeviceGraphsParameters = () => ({
  current: {},
  power: {},
  unitConsumed: { xAxisInterval: 1 },
  voltage: {},
  frequency: {},
  powerFactor: {},
});

export const demoDeviceHomePageInfo = () => [
  { dataType: "systemValues", valueType: "unitSaved" },
  { dataType: "systemValues", valueType: "unitConsumed" },
  { dataType: "systemValues", valueType: "unitSaving" },
];

// Meters whose 2nd and 3rd deviceId characters are one of these are left exactly as the API
// configured them - their own system values, graphs and tiles are used, not the reference
// meter's.
export const ORIGINAL_CONFIG_DEVICE_CODES = ["64", "28"];

export const keepsOriginalConfig = (deviceId) =>
  ORIGINAL_CONFIG_DEVICE_CODES.includes(String(deviceId ?? "").substring(1, 3));

/**
 * The rewritten devices end up in the persisted store, so the override has to be
 * reversible: the API's own config is kept under this key and put back the moment a device
 * stops qualifying (a new deviceId rule, the demo account logging out on a shared browser).
 * Without it, overriding is a one way trip - the original config is gone until the next
 * house fetch.
 */
const ORIGINAL_CONFIG = "__demoOriginalConfig";

const configOf = (device) => ({
  systemValues: device.systemValues,
  graphParameters: device.graphParameters,
  graphsParameters: device.graphsParameters,
  homePageInfo: device.homePageInfo,
});

export const restoreOriginalDeviceConfig = (device) => {
  if (!device?.[ORIGINAL_CONFIG]) return device;

  const restored = { ...device, ...device[ORIGINAL_CONFIG] };

  delete restored[ORIGINAL_CONFIG];

  return restored;
};

// Reference meter config applied on top of a single device.
export const applyDemoDeviceConfig = (device) => {
  if (!device || typeof device !== "object") return device;
  if (keepsOriginalConfig(device.deviceId)) return restoreOriginalDeviceConfig(device);

  return {
    ...device,
    [ORIGINAL_CONFIG]: device[ORIGINAL_CONFIG] || configOf(device),
    systemValues: demoDeviceSystemValues(),
    graphParameters: demoDeviceGraphParameters(),
    graphsParameters: demoDeviceGraphsParameters(),
    homePageInfo: demoDeviceHomePageInfo(),
  };
};

const rewriteHouse = (house) => {
  if (!house || !Array.isArray(house.rooms)) return house;

  return {
    ...house,
    rooms: house.rooms.map((room) =>
      Array.isArray(room?.energiSync)
        ? { ...room, energiSync: room.energiSync.map(applyDemoDeviceConfig) }
        : room
    ),
  };
};

/**
 * Rewrites every energiSync device of a house2 API response. Called at ingestion so the
 * data returned to the caller (the dashboard builds its device list straight off it,
 * without going through redux) is already overridden. `mobile` is the number being logged
 * in - during login the profile is not in the store yet, so the caller passes it explicitly.
 */
export const applyDemoConfigToHouse = (house, mobile) => {
  if (!house || !isDemoSavingsUser(mobile)) return house;

  return rewriteHouse(house);
};

/**
 * Memo keyed on the input object, one cache per rewrite. Two reasons: the store slices are
 * run through this on every action, so an unchanged slice has to come back as the exact
 * same reference or every connected component re-renders forever; and a rewritten value
 * maps to itself, which makes a second pass over it a no-op.
 */
const memoize = (rewrite) => {
  const cache = new WeakMap();

  return (value) => {
    if (!value || typeof value !== "object") return value;
    if (cache.has(value)) return cache.get(value);

    const result = rewrite(value);

    cache.set(value, result);
    cache.set(result, result);

    return result;
  };
};

// A device is a meter when it is tagged as one. The tag is missing on some paths that
// only ever hold meters, so an untagged device with a deviceId counts as a meter too.
const isMeter = (device) =>
  device?.deviceId !== undefined &&
  (device?.type === undefined || device?.type === config.deviceType.energiSync);

const rewriteDevice = (device) =>
  isMeter(device) ? applyDemoDeviceConfig(device) : device;

const memoRewriteHouse = memoize(rewriteHouse);

const memoRewriteHouses = memoize((houses) =>
  Object.keys(houses).reduce(
    (acc, id) => ({ ...acc, [id]: memoRewriteHouse(houses[id]) }),
    {}
  )
);

// deviceId -> the houseData copy of that meter, which is the copy the API last refreshed.
const memoConfigIndex = memoize((house) => {
  const byId = {};

  (house?.rooms || []).forEach((room) =>
    (room?.energiSync || []).forEach((device) => {
      if (device?.deviceId !== undefined) byId[device.deviceId] = device;
    })
  );

  return byId;
});

/**
 * dropdownSelectionData keeps its own copy of the selected device and of the room's device
 * list, and those copies are only rebuilt when the room or the device changes - refreshing
 * the house does not touch them. So the config is taken from the houseData copy whenever
 * the house has the device, which stops a stale copy from outliving the data it came from.
 * Devices the house does not know about (another property, non meters) just follow the rule.
 */
const syncDevice = (device, byId) => {
  const fromHouse = byId[device?.deviceId];

  if (!fromHouse) return rewriteDevice(device);
  if (
    device.systemValues === fromHouse.systemValues &&
    device.graphParameters === fromHouse.graphParameters &&
    device.graphsParameters === fromHouse.graphsParameters &&
    device.homePageInfo === fromHouse.homePageInfo
  ) {
    return device;
  }

  const synced = { ...device, ...configOf(fromHouse) };

  if (fromHouse[ORIGINAL_CONFIG]) {
    synced[ORIGINAL_CONFIG] = fromHouse[ORIGINAL_CONFIG];
  } else {
    delete synced[ORIGINAL_CONFIG];
  }

  return synced;
};

// Two inputs, so a single entry cache rather than the keyed-on-one-object memo above.
let dropdownCache = {};

const syncDropdown = (dropdown, byId) => {
  if (!dropdown || typeof dropdown !== "object") return dropdown;
  if (dropdownCache.input === dropdown && dropdownCache.byId === byId) {
    return dropdownCache.output;
  }

  const devices = Array.isArray(dropdown.devices)
    ? dropdown.devices.map((device) => syncDevice(device, byId))
    : dropdown.devices;
  const devicesUnchanged =
    !Array.isArray(devices) ||
    devices.every((device, index) => device === dropdown.devices[index]);
  const selected = syncDevice(dropdown.selectedDeviceDetails, byId);

  const output =
    devicesUnchanged && selected === dropdown.selectedDeviceDetails
      ? dropdown
      : {
        ...dropdown,
        devices: devicesUnchanged ? dropdown.devices : devices,
        selectedDeviceDetails: selected,
      };

  dropdownCache = { input: dropdown, byId, output };

  return output;
};

const memoRewriteState = memoize((state) => {
  const houseData = memoRewriteHouse(state.houseData);

  return {
    ...state,
    houseData,
    dropdownSelectionData: syncDropdown(
      state.dropdownSelectionData,
      memoConfigIndex(houseData) || {}
    ),
    mutipleHouse: memoRewriteHouses(state.mutipleHouse),
  };
});

/**
 * Applied to the whole store on every action. The ingestion hook in fetchHouseDetails only
 * covers a fresh house fetch, but houseData and dropdownSelectionData are persisted, so an
 * already logged in session rehydrates the original device config straight from localforage
 * and never re-fetches. Normalising here catches that path as well.
 */
export const applyDemoConfigToState = (state) => {
  // The mobile is read off the state that is being reduced, never off the store: this runs
  // while the store is still being created, so store.getState() is not available yet.
  const mobile = state?.login?.profile?.mobile;

  if (!mobile || !isDemoSavingsUser(mobile)) return state;

  return memoRewriteState(state);
};
